import { isHttpError } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type ISystemPreferencesRepository,
  type SystemPreferenceMap,
} from '@webapp/platform/iam/business-logic/interfaces/ISystemPreferencesRepository'
import { SystemPreferenceKeys } from '@webapp/platform/iam/entities/enums/SystemPreferenceKeys'
import {
  DEFAULT_PO_BILL_TO_CONTACT,
  DEFAULT_PO_BILL_TO_PRIMARY_CATEGORY,
  type SystemPreferenceConstructor,
} from '@webapp/platform/iam/entities/SystemPreference'
import { SystemPreferenceNotFoundError } from './SystemPreferenceNotFoundError'
import { type POBillToContactApiModel } from '../../api-models/POBillToContactApiModel'
import { type POPDFHiddenCustomFieldApiModel } from '../../api-models/POPDFHiddenCustomFieldApiModel'
import { type SystemPreferencesAPIModel } from '../../api-models/SystemPreferencesAPIModel'
import {
  mapPOBillToContactApiModel,
  mapPOBillToContactToApiModel,
} from '../../mappers/mapPOBillToContact'
import { mapPOBillToPrimaryCategoryApiModel } from '../../mappers/mapPOBillToPrimaryCategory'
import {
  mapPOPDFHiddenCustomFieldApiModel,
  mapPOPDFHiddenCustomFieldToApiModel,
} from '../../mappers/mapPOPDFHiddenCustomField'
import { type GetSystemPreferencesResponse } from '../../types/getSystemPreferencesResponse'
import { type UpdateSystemPreferencesResponse } from '../../types/updateSystemPreferencesResponse'

interface Dependency {
  httpClient: IHttpClient
}

const isPOBillToContactApiModel = (
  value: unknown
): value is POBillToContactApiModel =>
  typeof value === 'object' &&
  value !== null &&
  'mode' in value &&
  'user_id' in value

const isSystemPreferenceItem = <K extends keyof SystemPreferenceMap>(
  systemPreference: unknown,
  key: K
): systemPreference is SystemPreferenceConstructor<SystemPreferenceMap[K]> => {
  return (
    typeof systemPreference === 'object' &&
    systemPreference !== null &&
    'key' in systemPreference &&
    (systemPreference as { key: unknown }).key === key
  )
}

// The hidden-map and bill-to-contact preferences are the snake_case
// preference values — map them to their domain shape on reads and back to
// the wire shape on writes so the raw shape never leaks past the repository.
// The bill-to values fall back to their defaults whenever the stored value
// cannot be interpreted (an out-of-range int, a null or non-object contact):
// the kvstore column is nullable, and a raw value must never reach a consumer
// typed as the domain shape.
const mapPreferenceValueFromApi = (
  systemPreference: SystemPreferencesAPIModel | undefined
): SystemPreferencesAPIModel | undefined => {
  if (
    systemPreference?.key ===
      SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN &&
    Array.isArray(systemPreference.value)
  ) {
    return {
      ...systemPreference,
      value: (systemPreference.value as POPDFHiddenCustomFieldApiModel[]).map(
        mapPOPDFHiddenCustomFieldApiModel
      ),
    }
  }

  if (systemPreference?.key === SystemPreferenceKeys.PO_BILL_TO_LOCATION) {
    return {
      ...systemPreference,
      value:
        typeof systemPreference.value === 'number'
          ? mapPOBillToPrimaryCategoryApiModel(systemPreference.value)
          : DEFAULT_PO_BILL_TO_PRIMARY_CATEGORY,
    }
  }

  if (systemPreference?.key === SystemPreferenceKeys.PO_BILL_TO_CONTACT) {
    return {
      ...systemPreference,
      value: isPOBillToContactApiModel(systemPreference.value)
        ? mapPOBillToContactApiModel(systemPreference.value)
        : DEFAULT_PO_BILL_TO_CONTACT,
    }
  }

  return systemPreference
}

// Indexing the mapped type with the generic key narrows each mapper's input
// to that preference's domain value, so a wrong-shape mapper fails to compile.
const toApiMappers: {
  [K in keyof SystemPreferenceMap]?: (
    value: SystemPreferenceMap[K]['value']
  ) => unknown
} = {
  [SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN]: (value) =>
    value.map(mapPOPDFHiddenCustomFieldToApiModel),
  [SystemPreferenceKeys.PO_BILL_TO_CONTACT]: mapPOBillToContactToApiModel,
}

const mapPreferenceValueToApi = <K extends keyof SystemPreferenceMap>(
  key: K,
  value: SystemPreferenceMap[K]['value']
): unknown => {
  const mapper = toApiMappers[key]

  return mapper ? mapper(value) : value
}

export const makeSystemPreferencesRepository = ({
  httpClient,
}: Dependency): ISystemPreferencesRepository => ({
  // Resolves `null` when the preference was never set: the kvstore signals
  // that either as a 404 or by omitting the key from the GET-all response.
  // Anything else propagates, so a failure is never read as a default.
  getSystemPreference: async <K extends keyof SystemPreferenceMap>(
    key: K
  ): Promise<Nullable<SystemPreferenceConstructor<SystemPreferenceMap[K]>>> => {
    let systemPreferences: SystemPreferencesAPIModel[]

    try {
      const res = await httpClient.get<GetSystemPreferencesResponse>(
        `/api/v3/kvstore/system-preferences/`
      )
      systemPreferences = res.data.data
    } catch (error) {
      if (isHttpError(error) && error.status === 404) {
        return null
      }
      throw error
    }

    const systemPreference = mapPreferenceValueFromApi(
      systemPreferences.find((item) => item.key === key)
    )

    return isSystemPreferenceItem(systemPreference, key)
      ? systemPreference
      : null
  },

  updateSystemPreference: async <K extends keyof SystemPreferenceMap>(
    key: K,
    value: SystemPreferenceMap[K]['value']
  ): Promise<SystemPreferenceConstructor<SystemPreferenceMap[K]>> => {
    const res = await httpClient.post<UpdateSystemPreferencesResponse>(
      `/api/v3/kvstore/system-preferences/`,
      {
        key,
        value: mapPreferenceValueToApi(key, value),
      }
    )

    const systemPreference = mapPreferenceValueFromApi(
      res.data.data as unknown as SystemPreferencesAPIModel
    )

    if (isSystemPreferenceItem(systemPreference, key)) {
      return systemPreference
    } else {
      throw new SystemPreferenceNotFoundError(key)
    }
  },
})
